import { Box, Chip } from '@material-ui/core';
import { LatLng, LatLngBounds, Map } from 'leaflet';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IMapAttachmentMapParams, MapViewer, MapViewerControls } from '..';
import { extractPrefix } from '../../../utils';
import {
	Body1,
	ErrorBoundary,
	Header4,
	MapReference,
	MapSelectionMarker
} from '../../atoms';

import './attachment-location-selector.styles.scss';

export interface IAttachmentLocationSelectorProps {
	mapId: string;
	casperId: string;
	projection?: string;
	corner: LatLng;
	oppositeCorner: LatLng;
	position: LatLng | null;
	mapParams?: IMapAttachmentMapParams;
	setMapParams: (params: IMapAttachmentMapParams) => void;
	onPositionChange: (position?: LatLng) => void;
}

export function AttachmentLocationSelector(
	props: IAttachmentLocationSelectorProps
): ReactElement {
	const {
		mapId,
		casperId,
		projection,
		corner,
		oppositeCorner,
		position,
		mapParams,
		setMapParams,
		onPositionChange
	} = props;

	const { t } = useTranslation(['map', 'general']);

	const defaultPosition: LatLng = useMemo(() => {
		return new LatLngBounds(corner, oppositeCorner).getCenter();
	}, [mapId]);

	const selectorMapId: string = useMemo(() => {
		return `${mapId}-selector`;
	}, [mapId]);

	const [mapRef, setMapRef] = useState<Map>();

	useEffect(() => {
		if (mapRef && mapParams) {
			mapRef.setView(mapParams.center, mapParams.zoom);
		}
		if (mapRef) {
			mapRef.on('zoomend', () => {
				setMapParams({
					center: mapParams ? mapParams.center : mapRef.getCenter(),
					zoom: mapRef.getZoom()
				});
			});
			mapRef.on('dragend', () => {
				setMapParams({
					center: mapRef.getCenter(),
					zoom: mapParams ? mapParams.zoom : mapRef.getZoom()
				});
			});
		}
	}, [mapRef]);

	return (
		<Box className="attachment-location-selector">
			<Box className="attachment-location-selector-header">
				<Header4>{t('map:attachments.location.addLocation')}</Header4>
				<Chip label={t('general:notRequired')} />
			</Box>
			<Body1>{t('map:attachments.location.notAddLocationProceed')}</Body1>

			<Box className="attachment-location-selector-map-container">
				<ErrorBoundary>
					<MapViewer
						mapId={selectorMapId}
						casperId={casperId}
						corner={corner}
						oppositeCorner={oppositeCorner}
						projection={projection}
					>
						<MapReference onMapReference={setMapRef} />
						<MapViewerControls
							prefix={extractPrefix(casperId)}
							mapId={selectorMapId}
							corner={corner}
							oppositeCorner={oppositeCorner}
							options={{
								drawingControls: false,
								fullScreen: false,
								legend: false,
								mapActions: false
							}}
						/>
						<MapSelectionMarker
							position={
								position !== null ? position : defaultPosition
							}
							onMove={onPositionChange}
							hasMoved={position !== null}
						/>
					</MapViewer>
				</ErrorBoundary>
			</Box>
		</Box>
	);
}
